import React from 'react';
import classNames from 'classnames';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import PropTypes from 'prop-types';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { useNoteContext } from 'Context/NoteContext';
import { useNoteListStateContext } from './NoteListContext';
import { groupResourcesByType } from './util';

const styles = (theme) => ({
  root: {
    display: 'flex',
    flex: 3,
    backgroundColor: theme.palette.facet.whiteSmoke,
    padding: theme.spacing(2, 1),
    overflowY: 'auto',
  },
  accordionList: {
    display: 'flex',
    flexDirection: 'column',
  },
  accordion: {
    backgroundColor: theme.palette.facet.whiteSmoke,
    width: '100%',
    position: 'unset',
    padding: theme.spacing(1),
  },
  accordionRoot: {
    '&$expanded': {
      margin: theme.spacing(0),
    },
  },
  expanded: {},
  content: {
    margin: theme.spacing(0),
    '&$expanded': {
      margin: theme.spacing(0),
    },
  },
  accordionSummaryRoot: {
    padding: theme.spacing(0),
    borderRadius: theme.spacing(0.5),
    minHeight: 'unset',
    '&$expanded': {
      minHeight: 'unset',
    },
  },
  accordionSummaryContent: {
    margin: theme.spacing(0),
    padding: theme.spacing(0),
    '&Mui-expanded': {
      margin: theme.spacing(0),
    },
  },
  accordionDetails: {
    display: 'flex',
    flexDirection: 'column',
  },
  accordionDetailsRoot: {
    padding: theme.spacing(0, 0, 0, 3),
  },
  noteSubject: {
    margin: theme.spacing(0.75, 0),
    padding: theme.spacing(0.25, 0.5),
    borderRadius: theme.spacing(0.5),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.facet.accentGray,
    },
  },
  selected: {
    color: theme.palette.facet.accentGreen,
  },
});

const LeftNav = ({ classes, selectedNote, setScrollTo }) => {
  const { resources } = useNoteListStateContext();
  const { noteResourceObject } = useNoteContext();
  const [expanded, setExpanded] = React.useState('');
  const {
    rootNodeProps,
    logEvent,
  } = useEventTrackingLogEvent({
    nodeName: 'All Notes Left Nav',
  });

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  const resourcesGroupedByType = groupResourcesByType(resources);

  if (resourcesGroupedByType.length === 0) {
    return (
      <div className={classes.root}>
        <div className={classes.accordionList}>
          <Typography variant="h3">No Notes Found</Typography>
        </div>
      </div>
    );
  }

  const accordions = resourcesGroupedByType.map((group, i) => {
    let headerIsSelected = false;
    const notes = group.notes.filter(resource => resource.id() !== noteResourceObject.noteResource?.id()).map(resource => {
      const isSelected = `Note-${resource.id()}` === selectedNote;
      if (isSelected) {
        headerIsSelected = true;
      }

      const navigateToNote = () => {
        logEvent('Clicked note subject in all notes navigation');
        setScrollTo(`Note-${resource.id()}`);
      };

      return (
        <Typography
          variant="h5"
          id={`Note-Nav-${resource.id()}`}
          className={classNames(classes.noteSubject, isSelected ? classes.selected : {})}
          onClick={navigateToNote}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              navigateToNote();
              // Set focus on the new note
              const noteDomElement = document.getElementById(`Note-${resource.id()}`);
              if (noteDomElement) {
                noteDomElement.focus();
              }
            }
          }}
          tabIndex="0"
        >
          {resource.attr('noteSubject')}
        </Typography>
      );
    });

    const isExpanded = expanded === `Accordion-${group.id}` || (expanded === '' && i === 0);

    return (
      <Accordion
        elevation={0}
        square
        expanded={isExpanded}
        onChange={handleChange(`Accordion-${group.id}`)}
        className={classes.accordion}
        classes={{ root: classes.accordionRoot, expanded: classes.expanded }}
      >
        <AccordionSummary id={`Accordion-${group.id}`} classes={{ root: classes.accordionSummaryRoot, content: classes.content, expanded: classes.expanded }}>
          { !isExpanded && <ExpandMoreIcon /> }
          { isExpanded && <ExpandLessIcon /> }
          <Typography variant="h3" className={classNames(classes.heading, headerIsSelected ? classes.selected : {})}>{group.name}</Typography>
        </AccordionSummary>
        <AccordionDetails classes={{ root: classes.accordionDetailsRoot }}>
          <div className={classes.accordionDetails}>
            {notes}
          </div>
        </AccordionDetails>
      </Accordion>
    );
  });


  return (
    <div className={classes.root} {...rootNodeProps}>
      <div className={classes.accordionList}>
        {accordions}
      </div>
    </div>
  );
};

LeftNav.propTypes = {
  classes: PropTypes.object.isRequired,
  selectedNote: PropTypes.string.isRequired,
  setScrollTo: PropTypes.func.isRequired,
};

export default withStyles(styles)(LeftNav);
